import React, { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import styles from './KebabMenu.module.scss'

export interface KebabMenuItem {
  label: string
  onClick: () => void
}

const KebabMenu: React.FC<{ items: KebabMenuItem[] }> = ({ items }) => {
  const [open, setOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 })

  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return
    const rect = buttonRef.current.getBoundingClientRect()
    setPos({ top: rect.bottom, left: rect.right })
  }, [])

  useEffect(() => {
    if (!open) return
    updatePosition()
    const handleClick = (e: globalThis.MouseEvent) => {
      if (
        buttonRef.current?.contains(e.target as globalThis.Node) ||
        menuRef.current?.contains(e.target as globalThis.Node)
      ) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    window.addEventListener('scroll', () => setOpen(false), true)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      window.removeEventListener('scroll', () => setOpen(false), true)
    }
  }, [open, updatePosition])

  return (
    <div className={styles.kebab}>
      <button ref={buttonRef} className={styles.kebabButton} onClick={() => setOpen(o => !o)} aria-label="Actions">
        ⋮
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          className={styles.kebabMenu}
          style={{ top: pos.top, left: pos.left }}
        >
          {items.map(item => (
            <button
              key={item.label}
              className={styles.kebabMenuItem}
              onClick={() => { item.onClick(); setOpen(false) }}
            >
              {item.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  )
}

export default KebabMenu
